import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Two redesign directions from the "Adopt-A-Coward Redesign" mockup:
 * - evidence-board: kraft paper, exhibit tags, red thread connecting related stories
 * - field-report: ruled grid, numbered form fields, serials and stamps
 */
export type CaseFileStyle = 'evidence-board' | 'field-report';

const STORAGE_KEY = 'aac_case_file_style';

interface CaseFileStyleContextValue {
  style: CaseFileStyle;
  setStyle: (style: CaseFileStyle) => void;
  toggle: () => void;
}

const CaseFileStyleContext = createContext<CaseFileStyleContextValue | undefined>(undefined);

export const CaseFileStyleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [style, setStyleState] = useState<CaseFileStyle>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === 'field-report' ? 'field-report' : 'evidence-board';
    } catch {
      return 'evidence-board';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, style);
    } catch {
      // ignore storage errors (private browsing, etc.)
    }
  }, [style]);

  const setStyle = (next: CaseFileStyle) => setStyleState(next);
  const toggle = () => setStyleState((prev) => (prev === 'evidence-board' ? 'field-report' : 'evidence-board'));

  return (
    <CaseFileStyleContext.Provider value={{ style, setStyle, toggle }}>
      {children}
    </CaseFileStyleContext.Provider>
  );
};

export function useCaseFileStyle(): CaseFileStyleContextValue {
  const ctx = useContext(CaseFileStyleContext);
  if (!ctx) throw new Error('useCaseFileStyle must be used within a CaseFileStyleProvider');
  return ctx;
}
